/**
 * ProjectDetailPage — overview of a single project.
 *
 * Sections:
 *   • Header: name (editable), status, active pill, dates
 *   • Description (editable inline)
 *   • Honest empty-state cards for what 1C/1D will populate:
 *     Recent conversations · Decisions · Ideas · Tasks · Memory · Unresolved
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Archive, RotateCcw, Edit3, Check, X, Loader2, ArrowLeft,
    MessagesSquare, CheckCircle2, Lightbulb, ListTodo, Brain, AlertOctagon,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { getProject, updateProject, archiveProject, unarchiveProject } from "@/lib/projects";
import { useActiveProject } from "@/context/ActiveProjectContext";

function formatDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
}

function EditableField({ testId, label, value, onSave, multiline = false, maxLength = 500 }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value ?? "");
    const [busy, setBusy] = useState(false);

    useEffect(() => { setVal(value ?? ""); }, [value]);

    const commit = async () => {
        if (val === (value ?? "")) { setEditing(false); return; }
        setBusy(true);
        try { await onSave(val); setEditing(false); }
        finally { setBusy(false); }
    };

    if (!editing) {
        return (
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim mb-1">
                        {label}
                    </div>
                    <div
                        data-testid={`${testId}-value`}
                        className={`font-sans ${multiline ? "text-[14px] text-qoryn-textDim whitespace-pre-wrap" : "text-[16px] text-qoryn-text"}`}
                    >
                        {value || <span className="italic text-qoryn-textMuted">not set</span>}
                    </div>
                </div>
                <button
                    type="button"
                    data-testid={`${testId}-edit`}
                    onClick={() => setEditing(true)}
                    className="h-7 w-7 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.04] qoryn-focus"
                    aria-label={`Edit ${label}`}
                >
                    <Edit3 className="h-3 w-3" strokeWidth={1.75} />
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">{label}</div>
            {multiline ? (
                <textarea
                    data-testid={`${testId}-input`}
                    autoFocus
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    maxLength={maxLength}
                    rows={3}
                    className="w-full px-3 py-2 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text resize-none qoryn-focus"
                />
            ) : (
                <input
                    data-testid={`${testId}-input`}
                    autoFocus
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    maxLength={maxLength}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[15px] text-qoryn-text qoryn-focus"
                />
            )}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    data-testid={`${testId}-save`}
                    onClick={commit}
                    disabled={busy}
                    className="h-8 px-3 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase hover:brightness-110 disabled:opacity-50 qoryn-focus"
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                </button>
                <button
                    type="button"
                    onClick={() => { setEditing(false); setVal(value ?? ""); }}
                    className="h-8 px-3 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase qoryn-focus"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function SectionEmpty({ icon, title, note }) {
    return <EmptyState icon={icon} title={title} footnote={note} />;
}

export default function ProjectDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeId, setActiveId, refresh } = useActiveProject();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try { setProject(await getProject(id)); }
        catch (e) { setError(e?.message || String(e)); }
        finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const saveName = useCallback(async (name) => {
        const updated = await updateProject(id, { name });
        setProject(updated); refresh();
    }, [id, refresh]);
    const saveDesc = useCallback(async (description) => {
        const updated = await updateProject(id, { description });
        setProject(updated); refresh();
    }, [id, refresh]);
    const doArchive = useCallback(async () => {
        const updated = await archiveProject(id);
        setProject(updated); refresh();
    }, [id, refresh]);
    const doUnarchive = useCallback(async () => {
        const updated = await unarchiveProject(id);
        setProject(updated); refresh();
    }, [id, refresh]);

    if (loading) {
        return (
            <div className="py-24 flex items-center justify-center" data-testid="project-detail-loading">
                <Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" />
            </div>
        );
    }
    if (error || !project) {
        return (
            <EmptyState
                icon={AlertOctagon}
                title="Project not found."
                body={error || "This project either doesn't exist or you don't have access to it."}
                footnote={<button onClick={() => navigate("/projects")} className="text-qoryn-cyan hover:underline">Back to projects</button>}
            />
        );
    }

    const isActive = project.id === activeId;

    return (
        <div data-testid="project-detail-page" className="space-y-8">
            <div className="flex items-start gap-4">
                <button
                    type="button"
                    onClick={() => navigate("/projects")}
                    className="h-8 w-8 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] qoryn-focus"
                    aria-label="Back to projects"
                >
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
                </button>
                <div className="flex-1 min-w-0 space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            {project.slug}
                        </span>
                        {isActive && (
                            <span className="font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-cyan bg-qoryn-cyan/10 rounded-full px-1.5 py-0.5">
                                Active
                            </span>
                        )}
                        {project.status === "archived" && (
                            <span className="font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-textMuted bg-white/[0.04] rounded-full px-1.5 py-0.5">
                                Archived
                            </span>
                        )}
                    </div>
                    <EditableField
                        testId="project-name"
                        label="Name"
                        value={project.name}
                        onSave={saveName}
                        maxLength={80}
                    />
                    <EditableField
                        testId="project-description"
                        label="Description"
                        value={project.description || ""}
                        onSave={saveDesc}
                        multiline
                        maxLength={500}
                    />
                    <div className="flex items-center gap-4 flex-wrap font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textMuted">
                        <span>Created {formatDate(project.created_at)}</span>
                        <span>·</span>
                        <span>Updated {formatDate(project.updated_at)}</span>
                        {project.archived_at && (<><span>·</span><span>Archived {formatDate(project.archived_at)}</span></>)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {!isActive && project.status === "active" && (
                            <button
                                type="button"
                                data-testid="project-detail-set-active"
                                onClick={() => setActiveId(project.id)}
                                className="h-8 px-3 rounded-md border border-qoryn-cyan/60 text-qoryn-cyan hover:bg-qoryn-cyan/10 font-mono-ui text-[10px] tracking-widest uppercase qoryn-focus"
                            >
                                Make active project
                            </button>
                        )}
                        {project.status === "active" ? (
                            <button
                                type="button"
                                data-testid="project-detail-archive"
                                onClick={doArchive}
                                className="h-8 px-3 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 qoryn-focus"
                            >
                                <Archive className="h-3 w-3" /> Archive
                            </button>
                        ) : (
                            <button
                                type="button"
                                data-testid="project-detail-unarchive"
                                onClick={doUnarchive}
                                className="h-8 px-3 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 qoryn-focus"
                            >
                                <RotateCcw className="h-3 w-3" /> Restore
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SectionEmpty icon={MessagesSquare} title="No recent conversations." note="voice sessions land here in phase 1c" />
                <SectionEmpty icon={CheckCircle2}  title="No decisions logged." note="captured from conversations in phase 1c" />
                <SectionEmpty icon={Lightbulb}     title="No ideas captured." note="captured from conversations in phase 1c" />
                <SectionEmpty icon={ListTodo}      title="No tasks yet." note="captured from conversations in phase 1c" />
                <SectionEmpty icon={Brain}         title="No project memory." note="approved memories land here in phase 1d" />
                <SectionEmpty icon={AlertOctagon}  title="No unresolved items." note="derived from decisions & tasks in phase 1c" />
            </div>
        </div>
    );
}
